<script setup lang="ts">
/**
 * "Know when the company opens your CV": the link for one company
 * (utils/applicationLink.ts) on the marketing pages, with a screenshot of
 * its insights in the page's language. Light and dark captures are swapped
 * with CSS, as in HomepageAnalyticsSection. The screenshots come from
 * scripts/render-link-insights-screenshots.mjs; re-run it after changing the
 * insights' look or texts.
 */
const props = withDefaults(defineProps<{
  /** `surface-alt` on pages where the section before it is white. */
  alt?: boolean
}>(), { alt: false })

const { t, locale } = useI18n()
const { isAuthenticated } = useAuthUser()

const shot = computed(() => (['en', 'cs', 'de'].includes(locale.value) ? locale.value : 'en'))
const ctaPath = computed(() => (isAuthenticated.value ? '/applications' : '/pricing#plans'))

const points = [
  { id: 'opened', icon: 'bi-bell' },
  { id: 'read', icon: 'bi-book' },
  { id: 'passed', icon: 'bi-people' },
  { id: 'next', icon: 'bi-signpost-2' },
] as const
</script>

<template>
  <section class="sofax relative overflow-hidden" :class="props.alt ? 'surface-alt' : 'bg-white dark:bg-gray-950'">
    <div class="section-space">
      <div class="shell">
        <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div class="img-shadow mx-auto w-full max-w-[30rem]">
            <div class="overflow-hidden rounded-[0.875rem] border border-ink bg-white dark:border-gray-600 dark:bg-gray-800">
              <NuxtImg
                :src="`/link-tracking/${shot}-light.png`"
                :alt="t('linkTracking.alt')"
                width="640"
                height="903"
                format="webp"
                quality="85"
                loading="lazy"
                class="w-full dark:hidden"
              />
              <NuxtImg
                :src="`/link-tracking/${shot}-dark.png`"
                :alt="t('linkTracking.alt')"
                width="640"
                height="903"
                format="webp"
                quality="85"
                loading="lazy"
                class="hidden w-full dark:block"
              />
            </div>
          </div>

          <div>
            <div class="eyebrow mb-5">{{ t('linkTracking.eyebrow') }}</div>
            <h2 class="mb-5 !text-3xl md:!text-[2.5rem]">{{ t('linkTracking.title') }}</h2>
            <p class="!mb-8 text-lg">{{ t('linkTracking.text') }}</p>
            <ul class="mb-10 flex flex-col gap-5">
              <li v-for="point in points" :key="point.id" class="flex items-start gap-4">
                <SofaxIconTile :icon="point.icon" round size="sm" class="shrink-0" />
                <div>
                  <div class="display-heading display-heading-5">{{ t(`linkTracking.points.${point.id}.title`) }}</div>
                  <p class="!mb-0 mt-1 text-base">{{ t(`linkTracking.points.${point.id}.text`) }}</p>
                </div>
              </li>
            </ul>
            <SofaxBtn :to="ctaPath" :label="t('linkTracking.cta')" variant="accent" />
            <p class="!mb-0 mt-4 !text-sm text-muted dark:text-gray-400">{{ t('linkTracking.note') }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
